/**
 * Shared validation + normalisation for team registration fields.
 *
 * When a division has `is_team = true`, the registration form collects:
 *   - team_name: optional free text. Defaults to the registering player's
 *     full name (or guest name) when blank.
 *   - team_info: required free textarea listing team members.
 *
 * For non-team divisions, both fields are ignored (and stored as null).
 */

export const TEAM_NAME_MAX_LENGTH = 200;
export const TEAM_INFO_MAX_LENGTH = 2000;

export type TeamFieldsInput = {
  team_name?: unknown;
  team_info?: unknown;
};

export type ResolvedTeamFields = {
  team_name: string | null;
  team_info: string | null;
};

export type TeamFieldsByGroup = Record<string, TeamFieldsInput | undefined>;

export type TeamValidationError = {
  groupId: string;
  reason: 'team_info_required' | 'team_name_too_long' | 'team_info_too_long';
};

/**
 * Validate and normalise team fields for a single group.
 * Returns either resolved values (for insert) or a validation error.
 */
export function resolveTeamFields(
  group: { id: string; is_team: boolean | null },
  input: TeamFieldsInput | undefined,
  defaultTeamName: string,
): { ok: true; values: ResolvedTeamFields } | { ok: false; error: TeamValidationError } {
  if (!group.is_team) {
    return { ok: true, values: { team_name: null, team_info: null } };
  }

  const rawName = typeof input?.team_name === 'string' ? input.team_name.trim() : '';
  const rawInfo = typeof input?.team_info === 'string' ? input.team_info.trim() : '';

  if (!rawInfo) {
    return { ok: false, error: { groupId: group.id, reason: 'team_info_required' } };
  }
  if (rawName.length > TEAM_NAME_MAX_LENGTH) {
    return { ok: false, error: { groupId: group.id, reason: 'team_name_too_long' } };
  }
  if (rawInfo.length > TEAM_INFO_MAX_LENGTH) {
    return { ok: false, error: { groupId: group.id, reason: 'team_info_too_long' } };
  }

  return {
    ok: true,
    values: {
      team_name: rawName || defaultTeamName.trim() || null,
      team_info: rawInfo,
    },
  };
}
